import { SiteHeader } from "../components/platform/PlatformChrome";
import { Footer } from "../components/landing/Footer";
import { useReveal } from "../lib/hooks";
import { formatBytes, formatPublished, useAppRelease } from "../lib/appRelease";

/**
 * `/app` — where you get the Veda Union app. A "secret" route, omitted from nav
 * (like `/sankalpa` and `/panchang`): it is a link you hand to someone, not a
 * destination the site advertises.
 *
 * Landing register, not the app register — this is a public page, read by people
 * who are not signed in and may never have seen the platform.
 *
 * Nothing about a release is hardcoded here. Each download URL is a stable
 * filename that every release overwrites, and the version, size, date and
 * checksum come from `GET /api/app/release`, which reads the manifest published
 * beside each build (`scripts/publish-apk.sh`, `scripts/publish-desktop.sh`).
 * So the page is always describing the files it links to.
 *
 * Two audiences, one page, and the copy says which is which: the phone build is
 * for READING and the desktop build is for AUTHORING. Someone who wants to mark
 * a text should not download the phone app and find out afterwards.
 *
 * The SmartScreen warning is described BEFORE it happens, and only while
 * `signed` is false. Meeting an unexplained "unrecognised publisher" panel is
 * how a legitimate download starts looking like a mistake.
 */
export default function AppDownload() {
  const { ref } = useReveal<HTMLDivElement>();
  const { data, isLoading } = useAppRelease();
  const android = data?.android ?? null;
  const windows = data?.windows ?? null;

  return (
    <>
      <SiteHeader />
      <main className="pt-28 md:pt-36 pb-24">
        {/* ---------------------------------------------------------------- */}
        {/* Hero                                                             */}
        {/* ---------------------------------------------------------------- */}
        <section className="relative overflow-hidden">
          {/* Barely-there ॐ, the same atmospheric device the landing uses. */}
          <span
            className="absolute font-deva pointer-events-none select-none"
            aria-hidden
            style={{
              fontSize: "clamp(18rem, 34vw, 32rem)",
              color: "var(--color-violet)",
              opacity: 0.06,
              right: "-4%",
              top: "40%",
              transform: "translateY(-50%)",
              lineHeight: 0.8,
              zIndex: 0,
            }}
          >
            ॐ
          </span>

          <div ref={ref} className="container-edit relative reveal" style={{ zIndex: 1 }}>
            <div className="max-w-4xl">
              <p className="eyebrow flex items-center gap-3 mb-10">
                <span className="inline-block w-8 h-px bg-[var(--color-violet)]" />
                <span>The app</span>
              </p>
              <h1 className="font-display text-[clamp(2.8rem,8vw,6.5rem)] leading-[0.97] tracking-[-0.02em]">
                Your library,{" "}
                <span
                  className="font-display-italic"
                  style={{ color: "var(--color-violet)" }}
                >
                  offline
                </span>
                .
              </h1>
              <hr className="rule-violet w-12 my-10" />
              <p className="text-[1.2rem] leading-[1.55] text-[var(--color-ink-soft)] max-w-[54ch]">
                The whole platform, on your phone — the chants with their
                markings, your library, the courses and the calendar. It keeps
                working with no signal, on a train or in a temple, and quietly
                brings itself up to date whenever you do have one. You stay
                signed in; there is no logging in every time.
                {" "}There is a desktop version too, for writing rather than
                reading.
              </p>
            </div>
          </div>
        </section>

        {/* ---------------------------------------------------------------- */}
        {/* The two platforms                                                */}
        {/* ---------------------------------------------------------------- */}
        <section className="container-edit mt-20 md:mt-28">
          <div className="grid gap-10 lg:gap-14 lg:grid-cols-2 items-start">
            {/* ---- Android ------------------------------------------------ */}
            <article className="app-card">
              <p className="eyebrow-mute mb-6">Android</p>
              <h2 className="font-display text-[clamp(1.9rem,4vw,2.6rem)] leading-[1.05] tracking-[-0.015em]">
                Ready now
              </h2>
              <p className="mt-5 text-[1.02rem] leading-[1.6] text-[var(--color-ink-soft)]">
                A direct download — not through the Play Store, so there is no
                account to make and nothing to sign up for.
              </p>

              <div className="mt-9">
                {android ? (
                  <a href={android.url} className="btn-primary">
                    <span>Download for Android</span>
                    <span className="btn-arrow-icon" aria-hidden>
                      →
                    </span>
                  </a>
                ) : (
                  <p
                    className="font-grotesk text-[0.9rem] text-[var(--color-ink-mute)]"
                    aria-live="polite"
                  >
                    {isLoading
                      ? "Checking for the current build…"
                      : "The download is briefly unavailable — please try again shortly."}
                  </p>
                )}
              </div>

              {android && (
                <dl className="app-meta mt-8">
                  <div>
                    <dt>Version</dt>
                    <dd>
                      {android.version}{" "}
                      <span style={{ color: "var(--color-ink-light)" }}>
                        (build {android.versionCode})
                      </span>
                    </dd>
                  </div>
                  <div>
                    <dt>Size</dt>
                    <dd>{formatBytes(android.sizeBytes)}</dd>
                  </div>
                  <div>
                    <dt>Updated</dt>
                    <dd>{formatPublished(android.publishedAt)}</dd>
                  </div>
                  <div>
                    <dt>Requires</dt>
                    <dd>Android 7.0 or later</dd>
                  </div>
                </dl>
              )}

              <h3 className="app-h3 mt-10">Installing it</h3>
              <ol className="app-steps">
                <li>Open the download on your phone.</li>
                <li>
                  Your browser will warn that this kind of file can be harmful —
                  that warning appears for every app installed outside the Play
                  Store. Choose <strong>Download anyway</strong>.
                </li>
                <li>
                  Open the downloaded file. Android will ask whether to allow
                  installs from your browser; allow it, then{" "}
                  <strong>Install</strong>.
                </li>
              </ol>
              <p className="app-note">
                Updating later is the same download — install it over the top and
                everything you have saved stays where it is.
              </p>

              {android && (
                <details className="app-details">
                  <summary>Verify the download</summary>
                  <p>
                    If you would rather check the file is exactly the one we
                    published, its SHA-256 is:
                  </p>
                  <code>{android.sha256}</code>
                </details>
              )}
            </article>

            {/* ---- iPhone ------------------------------------------------- */}
            <article className="app-card">
              <p className="eyebrow-mute mb-6">iPhone &amp; iPad</p>
              <h2 className="font-display text-[clamp(1.9rem,4vw,2.6rem)] leading-[1.05] tracking-[-0.015em]">
                Coming soon
              </h2>
              <p className="mt-5 text-[1.02rem] leading-[1.6] text-[var(--color-ink-soft)]">
                An App Store release is on its way. In the meantime you can add
                Veda Union to your home screen and get very nearly the same
                thing — its own icon, no browser bars, and the same offline
                reading.
              </p>

              <h3 className="app-h3 mt-9">Add it to your home screen</h3>
              <ol className="app-steps">
                <li>
                  Open <strong>vedaunion.org</strong> in <strong>Safari</strong>{" "}
                  — this only works in Safari, not Chrome.
                </li>
                <li>
                  Tap the <strong>Share</strong> button (the square with an arrow
                  pointing up).
                </li>
                <li>
                  Choose <strong>Add to Home Screen</strong>, then{" "}
                  <strong>Add</strong>.
                </li>
              </ol>
              <p className="app-note">
                Open it once while you have a connection so it can save your
                library for offline reading.
              </p>
            </article>
          </div>
        </section>

        {/* ---------------------------------------------------------------- */}
        {/* Desktop — a different reason to install                          */}
        {/* ---------------------------------------------------------------- */}
        <section className="container-edit mt-20 md:mt-28">
          <div className="grid gap-10 lg:gap-14 lg:grid-cols-2 items-start">
            <article className="app-card">
              <p className="eyebrow-mute mb-6">Windows</p>
              <h2 className="font-display text-[clamp(1.9rem,4vw,2.6rem)] leading-[1.05] tracking-[-0.015em]">
                {windows ? "For authoring" : "Almost ready"}
              </h2>
              <p className="mt-5 text-[1.02rem] leading-[1.6] text-[var(--color-ink-soft)]">
                The same platform as a real application, for the work the phone is
                the wrong shape for: writing and marking texts, importing and
                exporting Word and PDF, and cutting a recording to the words —
                over your own files, with no upload limit.
              </p>

              <div className="mt-9">
                {windows ? (
                  <a href={windows.url} className="btn-primary">
                    <span>Download for Windows</span>
                    <span className="btn-arrow-icon" aria-hidden>
                      →
                    </span>
                  </a>
                ) : (
                  <p
                    className="font-grotesk text-[0.9rem] text-[var(--color-ink-mute)]"
                    aria-live="polite"
                  >
                    {isLoading
                      ? "Checking for the current build…"
                      : "Not published yet — check back shortly."}
                  </p>
                )}
              </div>

              {windows && (
                <dl className="app-meta mt-8">
                  <div>
                    <dt>Version</dt>
                    <dd>{windows.version}</dd>
                  </div>
                  <div>
                    <dt>Size</dt>
                    <dd>{formatBytes(windows.sizeBytes)}</dd>
                  </div>
                  <div>
                    <dt>Updated</dt>
                    <dd>{formatPublished(windows.publishedAt)}</dd>
                  </div>
                  <div>
                    <dt>Requires</dt>
                    <dd>{windows.minOs ?? "Windows 10 or later, 64-bit"}</dd>
                  </div>
                </dl>
              )}

              <h3 className="app-h3 mt-10">Installing it</h3>
              <ol className="app-steps">
                <li>Open the download. It installs for you alone — no administrator password.</li>
                {windows && !windows.signed && (
                  <li>
                    Windows will show a blue <strong>SmartScreen</strong> panel saying
                    the publisher is unrecognised. That appears for every application
                    without a paid certificate, not because anything is wrong with this
                    one. Choose <strong>More info</strong>, then{" "}
                    <strong>Run anyway</strong> — and check the SHA-256 below first if
                    you would rather be sure.
                  </li>
                )}
                <li>That is all. It opens straight into the platform.</li>
              </ol>
              <p className="app-note">
                Updating later is the same download — install it over the top and
                everything you have saved stays where it is.
              </p>

              {windows && (
                <details className="app-details">
                  <summary>Verify the download</summary>
                  <p>
                    If you would rather check the file is exactly the one we
                    published, its SHA-256 is:
                  </p>
                  <code>{windows.sha256}</code>
                </details>
              )}
            </article>

            <article className="app-card">
              <p className="eyebrow-mute mb-6">Mac &amp; Linux</p>
              <h2 className="font-display text-[clamp(1.9rem,4vw,2.6rem)] leading-[1.05] tracking-[-0.015em]">
                Coming
              </h2>
              <p className="mt-5 text-[1.02rem] leading-[1.6] text-[var(--color-ink-soft)]">
                Both are built from the same source as the Windows version and
                behave the same way. The Mac build needs Apple's own signature
                before macOS will open it at all, which is the only thing holding
                it up.
              </p>
              <p className="app-note mt-9">
                Until then, everything except editing your own local files works in
                the browser — sign in at <strong>vedaunion.org</strong> and the
                editor is there.
              </p>
            </article>
          </div>
        </section>

        {/* ---------------------------------------------------------------- */}
        {/* What "offline" means, honestly                                    */}
        {/* ---------------------------------------------------------------- */}
        <section className="container-edit mt-20 md:mt-28">
          <div className="max-w-[62ch]">
            <p className="eyebrow-mute mb-6">Good to know</p>
            <p className="text-[1.05rem] leading-[1.65] text-[var(--color-ink-soft)]">
              Everything you have opened stays readable with no connection, and
              the chants — with their markings, in every script — are built into
              the app itself, so they are there from the very first launch.
              Pictures inside documents still need a connection the first time
              you see them. When you next have signal, the app catches up on its
              own: there is nothing to refresh and nothing to reinstall.
            </p>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
